import { ScheduledReportStatus } from '@flexible-reports/store/api/reports/types';
import {
  isAMCTemplateAnalysisEnabled,
  isBonoboAnalysisRfTemplateEnabled,
  isBonoboAnalysisEnabled,
  isBonoboFullFunnelAnalysisEnabled,
  isRoutingToAAO
} from '@flexible-reports/utils/app-utils';

const DEFAULT_AGENT_ID = 'ads_analysis_a3c_agent';
const AAO_AGENT_ID = 'ads_agent_orchestrator';

export const getAgentId = () => (isRoutingToAAO() ? AAO_AGENT_ID : DEFAULT_AGENT_ID);

const campaignsFields = [
  'advertiserAccount.id',
  'advertiserAccount.name',
  'adProduct.value',
  'campaign.id',
  'campaign.name',
  'budgetCurrency.value',
  'metric.impressions',
  'metric.viewableImpressions',
  'metric.clicks',
  'metric.ctr',
  'metric.vctr',
  'metric.totalCost',
  'metric.purchases',
  'metric.newToBrandPurchases',
  'metric.costPerPurchase',
  'metric.costPerNewToBrandPurchase',
  'metric.sales',
  'metric.longTermSales',
  'metric.roas',
  'metric.longTermRoas'
];

const targetingFields = [
  'advertiserAccount.id',
  'advertiserAccount.name',
  'adProduct.value',
  'campaign.id',
  'campaign.name',
  'budgetCurrency.value',
  'target.value',
  'target.matchType',
  'metric.impressions',
  'metric.viewableImpressions',
  'metric.clicks',
  'metric.ctr',
  'metric.vctr',
  'metric.totalCost',
  'metric.purchases',
  'metric.newToBrandPurchases',
  'metric.costPerPurchase',
  'metric.costPerNewToBrandPurchase',
  'metric.sales',
  'metric.longTermSales',
  'metric.roas',
  'metric.longTermRoas'
];

const reachAndFrequencyFields = [
  'advertiserAccount.id',
  'advertiserAccount.name',
  'campaign.id',
  'campaign.name',
  'metric.impressions',
  'metric.userReach',
  'metric.userFrequency1',
  'metric.userFrequency2',
  'metric.userFrequency3',
  'metric.userFrequency4',
  'metric.userFrequency5',
  'metric.userFrequency6',
  'metric.userFrequency7',
  'metric.userFrequency8',
  'metric.userFrequency9',
  'metric.userFrequency10Plus',
  'metric.averageUserImpressionFrequency'
];

const fullFunnelFields = [
  'date.value',
  'advertiserAccount.id',
  'advertiserAccount.name',
  'advertiserAccount.managerAccounts',
  'campaign.id',
  'campaign.globalCampaignId',
  'budgetCurrency.value',
  'metric.impressions',
  'metric.clicks',
  'metric.ctr',
  'metric.totalCost',
  'metric.purchases',
  'metric.newToBrandPurchases',
  'metric.costPerPurchase',
  'metric.costPerNewToBrandPurchase',
  'metric.sales',
  'metric.newToBrandSales',
  'metric.longTermSales',
  'metric.longTermSalesNewToBrandBrandedSearches',
  'metric.longTermSalesNewToBrandDetailPageViews',
  'metric.longTermSalesNewToBrandAddToCart',
  'metric.longTermSalesNewToBrandRepeatPurchases',
  'metric.roas',
  'metric.longTermRoas',
  'metric.newToBrandPurchasesPromoted',
  'metric.detailPageViews',
  'metric.newToBrandDetailPageViews',
  'metric.newToBrandAddToCart'
];

type SUPPORTED_ANALYSIS_TYPES = 'campaigns' | 'targeting' | 'reachAndFrequency' | 'fullFunnel';

const METADATA_ID: Record<SUPPORTED_ANALYSIS_TYPES, string> = {
  campaigns: 'd7dd145fcbb0d4b4b17b5bef648cad832dcfa1e564cd27332cd3418d3fa6c1a4',
  targeting: '7e8077f1de297921538489536781e486739eba459ca530d657f3d2b01a50a98d',
  reachAndFrequency: '411a043a71e8aeaa2a1d728f5b00c92c1cd9e3dcc56688f45a5b3e147c4f3887',
  fullFunnel: '70305b0b205285bbc552f606a32495b27173f2c9edef521a42807f0bafaf70fd'
};

export const getAnalysisMetadataId = (fields: string[]) => {
  const fieldSet = new Set(fields);

  if (targetingFields.every(field => fieldSet.has(field))) {
    return METADATA_ID.targeting;
  }

  if (campaignsFields.every(field => fieldSet.has(field))) {
    return METADATA_ID.campaigns;
  }

  if (reachAndFrequencyFields.every(field => fieldSet.has(field))) {
    return METADATA_ID.reachAndFrequency;
  }

  if (fullFunnelFields.every(field => fieldSet.has(field))) {
    return METADATA_ID.fullFunnel;
  }

  return '';
};

const BLOCKED_FIELDS_FOR_RF_ANALYSIS = ['hour.value', 'date.value', 'day.value', 'month.value', 'week.value', 'year.value'];
const isAvailableForRFAnalysis = (fieldSet: Set<string>) =>
  reachAndFrequencyFields.every(field => fieldSet.has(field)) && BLOCKED_FIELDS_FOR_RF_ANALYSIS.every(field => !fieldSet.has(field));

const isAvailableForFullFunnelAnalysis = (fieldSet: Set<string>) => fullFunnelFields.every(field => fieldSet.has(field));

export const isAvailableForAnalysis = (latestReportStatus: string, fields: string[], templateId?: string) => {
  const fieldSet = new Set(fields);
  const isRfAnalysisEligible = isBonoboAnalysisRfTemplateEnabled() && isAvailableForRFAnalysis(fieldSet);
  const isFullFunnelEligible = isBonoboFullFunnelAnalysisEnabled() && isAvailableForFullFunnelAnalysis(fieldSet);
  const isExpandedReports = (isAMCTemplateAnalysisEnabled() && !!templateId) || isRfAnalysisEligible || isFullFunnelEligible;
  return latestReportStatus === ScheduledReportStatus.COMPLETED && isExpandedReports;
};

export interface AnalysisTemplateConfig {
  metadataId: string;
  reportSource: 'ADS' | 'AMC';
}

export const ANALYSIS_SUPPORTED_TEMPLATES: Record<string, AnalysisTemplateConfig> = {
  campaign: {
    metadataId: 'd7dd145fcbb0d4b4b17b5bef648cad832dcfa1e564cd27332cd3418d3fa6c1a4',
    reportSource: 'ADS'
  },
  reach_and_frequency: {
    metadataId: '411a043a71e8aeaa2a1d728f5b00c92c1cd9e3dcc56688f45a5b3e147c4f3887',
    reportSource: 'ADS'
  },
  audience: {
    metadataId: 'da12662ba017362a51aa842109b0e383dc2f1b7d6106d8409cdbcc3fe4eec330',
    reportSource: 'ADS'
  },
  target: {
    metadataId: '7e8077f1de297921538489536781e486739eba459ca530d657f3d2b01a50a98d',
    reportSource: 'ADS'
  },
  // New to Brand Gateway Asins
  '942cc659aff502110d0ca5f42f6db934df1275fb31e3e54daee94a3901678177': {
    metadataId: '942cc659aff502110d0ca5f42f6db934df1275fb31e3e54daee94a3901678177',
    reportSource: 'ADS'
  },
  // Overlap by campaign groups
  '38a7ea0537f8205f55d5c5e0335fbf98801de5265a23f2359ab23060f9230bb7': {
    metadataId: '38a7ea0537f8205f55d5c5e0335fbf98801de5265a23f2359ab23060f9230bb7',
    reportSource: 'ADS'
  },
  // Time to conversion
  e4b1c626c51e5fb3365f76c54d3e3ec6da9c739b86c4a8b6614b776e208d9368: {
    metadataId: 'e4b1c626c51e5fb3365f76c54d3e3ec6da9c739b86c4a8b6614b776e208d9368',
    reportSource: 'ADS'
  },
  // Sponsored ads targeted keyword purchase path
  '55f5c75044a9bfe0114f5d8b69e9e3c42e2af2f10fd83e6c5c64e837d7146080': {
    metadataId: '55f5c75044a9bfe0114f5d8b69e9e3c42e2af2f10fd83e6c5c64e837d7146080',
    reportSource: 'ADS'
  },
  // ASIN Purchase Overlap for Upselling
  '49a5379dd736f7b2ea17157b87feb26274b487592662609310082a9f8e0019ec': {
    metadataId: '49a5379dd736f7b2ea17157b87feb26274b487592662609310082a9f8e0019ec',
    reportSource: 'ADS'
  }
};

// Template-level feature flags: maps template ID to its gate function.
// To add a new template behind a weblab, add an entry here.
const TEMPLATE_FEATURE_GATES: Record<string, () => boolean> = {
  reach_and_frequency: isBonoboAnalysisRfTemplateEnabled
};

export const getEnabledAnalysisTemplates = (): Record<string, AnalysisTemplateConfig> => {
  if (!isBonoboAnalysisEnabled()) return {};
  return Object.fromEntries(
    Object.entries(ANALYSIS_SUPPORTED_TEMPLATES).filter(([id]) => {
      const gate = TEMPLATE_FEATURE_GATES[id];
      return gate && gate();
    })
  );
};
